import { Card } from '@zond/ui';
import { BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttendancePeriodType,
  AverageSeriesData,
  useAttendanceData,
  UserSeriesData,
} from '../hooks/useAttendanceData';
import { AttendanceChartSkeleton } from './attendance/AttendanceChartSkeleton';
import { AttendanceControls } from './attendance/AttendanceControls';
import { AverageAttendanceChart } from './attendance/AverageAttendanceChart';
import { UserAttendanceChart } from './attendance/UserAttendanceChart';

// Component for displaying attendance chart
interface AttendanceChartProps {
  isLoading?: boolean;
  projectId: number;
}

export function AttendanceChart({ isLoading: externalLoading = false, projectId }: AttendanceChartProps) {
  const { t } = useTranslation('dashboard');
  const [selectedPeriod, setSelectedPeriod] = useState<AttendancePeriodType>('current_month');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showAverageOnly, setShowAverageOnly] = useState(true);

  // Use the custom hook to get all the data and functions
  const {
    apiData,
    chartData,
    formatXAxisDate,
    isLoading: dataLoading,
  } = useAttendanceData(selectedPeriod, selectedUsers, showAverageOnly, projectId);

  // Handler for display mode change
  const handleModeChange = (isAverage: boolean) => {
    setShowAverageOnly(isAverage);
  };

  // Handler for period change
  const handlePeriodChange = (value: AttendancePeriodType) => {
    setSelectedPeriod(value);
  };

  // Handler for user selection change
  const handleUserSelectionChange = (userId: number) => {
    const isSelected = selectedUsers.includes(userId);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // If loading, show skeleton
  if (externalLoading || dataLoading) {
    return <AttendanceChartSkeleton />;
  }

  // If no data or empty data, show message
  if (!apiData || !chartData || (apiData.users.length === 0 && apiData.records.length === 0)) {
    return (
      <Card className="p-6 flex flex-col justify-between">
        <AttendanceControls
          selectedPeriod={selectedPeriod}
          showAverageOnly={showAverageOnly}
          onPeriodChange={handlePeriodChange}
          onModeChange={handleModeChange}
          hideDisplayModeSwitch={true}
        />

        <div className="flex flex-col items-center justify-center w-full h-[200px] p-6 rounded-lg">
          <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
            <BarChart3 className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col items-center text-center">
            <h2 className="text-lg font-semibold mb-2">{t('attendance.empty.title')}</h2>
            <p className="text-sm text-muted-foreground max-w-[300px]">{t('attendance.empty.description')}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 flex flex-col justify-between">
      <AttendanceControls
        selectedPeriod={selectedPeriod}
        showAverageOnly={showAverageOnly}
        onPeriodChange={handlePeriodChange}
        onModeChange={handleModeChange}
      />

      <div style={{ height: 250 }}>
        {showAverageOnly ? (
          // Average value chart
          <AverageAttendanceChart
            data={chartData as AverageSeriesData[]}
            formatXAxisDate={formatXAxisDate}
            selectedPeriod={selectedPeriod}
          />
        ) : (
          // Chart for individual users with built-in legend
          <UserAttendanceChart
            data={chartData as UserSeriesData[]}
            formatXAxisDate={formatXAxisDate}
            apiData={apiData}
            selectedUsers={selectedUsers}
            onUserSelectionChange={handleUserSelectionChange}
            selectedPeriod={selectedPeriod}
          />
        )}
      </div>
    </Card>
  );
}
